import React, { useState, useEffect } from 'react'
import { formatMessage } from '../../../../locale'
import Paper from '../../../../ui-kit/Paper/Paper'
import Button from '../../../../ui-kit/Button/Button'
import { paths } from '../../../../config/paths'
import { SurveyList } from './List/SurveyList'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store'
import {
  useExportSurveyResultsMutation,
  useGetSurveyQuery,
  useExportSurveyToPowerPointMutation,
} from '../../../../slices/surveyApiSlice'
import { useNavigate, useParams } from 'react-router-dom'
import { BackButton, Title } from './SurveyView.style'
import { LuArrowLeft } from 'react-icons/lu'
import ConfirmationModal from '../../../../ui-kit/Modal/ConfirmationModal'
import ModalContent from './ModalContent/ModalContent'
import Toast from '../../../../ui-kit/Toast/Toast'
import PowerPointSpinner from '../../../../ui-kit/Spinner/PowerPointSpinner'


const SurveyView: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>()
  const navigate = useNavigate()

  const { data: surveyData } = useGetSurveyQuery({ id: surveyId })
  const [exportSurveyResults, {isLoading: isExcelLoading}] = useExportSurveyResultsMutation()
  const [exportSurveyToPowerPoint, {isLoading:  isPowerPointLoading }] = useExportSurveyToPowerPointMutation()

  const { userCompany, userInfo, permissions, canExportSurvey } = useSelector(
    (state: RootState) => state.auth
  )

  const canDo = (permission: string) => {
    return permissions && !!permissions[permission]
  }

  const [openModal, setOpenModal] = useState(false)
  const [selectedType, setSelectedType] = useState<any>('')
  const [selectedDepartments, setSelectedDepartments] = useState<any>(null)
  const [selectedLanguage, setSelectedLanguage] = useState<any>('')
  const [showToastMessage, setShowToastMessage] = useState(false)

  const [deparmentDropdownOpen, setDepartmentDropdownOpen] = useState<boolean>(false)
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState<boolean>(false)

  const handleExport = async (surveyId: any) => {
    try {
      const blob = await exportSurveyResults({ id: surveyId }).unwrap()
      if (blob) {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'survey_results.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        return true // Return success indicator
      }
      return false // No blob returned
    } catch (error) {
      console.error('Error exporting survey results:', error)
      return false // Return failure indicator
    }
  }

const handleExportPowerPoint = async (surveyId: any) => {
  // Allow forward if either checkbox is checked, otherwise require both fields
  if (
    (!selectedDepartments?.value && !deparmentDropdownOpen) ||
    (!selectedLanguage?.value && !languageDropdownOpen)
  ) {
    setShowToastMessage(true)
    return false
  }

  try {
    const data = {
      departments: deparmentDropdownOpen
        ? [] 
        : [selectedDepartments.value],
      ...(languageDropdownOpen ? {} : { language: selectedLanguage?.value }),
    }

    const blob = await exportSurveyToPowerPoint({
      id: surveyId,
      data,
    }).unwrap()

    if (blob) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'survey_results.pptx'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      return true
    }
    return false
  } catch (error) {
    console.error('Error exporting survey to PowerPoint:', error)
    return false
  }
}

  async function handleExportFile(surveyId: any) {
    if (!selectedType?.value) {
      setShowToastMessage(true)
      return
    }

    try {
      let success = false

      if (selectedType.value === 'powerpoint') {
        success = await handleExportPowerPoint(surveyId)
      } else if (selectedType.value === 'excel') {
        success = await handleExport(surveyId)
      }

      // Only reset form and close modal if export was successful
      if (success) {
        setOpenModal(false)
        setSelectedType('')
        setSelectedDepartments(null)
        setSelectedLanguage('')
      }
    } catch (error) {
      console.error('Error exporting file:', error)
    }
  }

  useEffect(() => {
    if (showToastMessage) {
      const timer = setTimeout(() => {
        setShowToastMessage(false)
      }, 4500)

      return () => clearTimeout(timer)
    }
  }, [showToastMessage])

useEffect(() => {
  if (showToastMessage) {
    // Check if PowerPoint export is selected
    const isPowerPointType = selectedType?.value === 'powerpoint'

    const hasInputError =
      !selectedType?.value ||
      (isPowerPointType &&
        (
          (!selectedDepartments?.value && !deparmentDropdownOpen) ||
          (!selectedLanguage?.value && !languageDropdownOpen)
        )
      )

    if (!hasInputError) {
      setShowToastMessage(false) // Close immediately if no errors
      return
    }

    // Auto-hide the toast after delay
    const timer = setTimeout(() => {
      setShowToastMessage(false)
    }, 4500)

    return () => clearTimeout(timer)
  }
}, [
  showToastMessage,
  selectedType,
  selectedDepartments,
  selectedLanguage,
  deparmentDropdownOpen,
  languageDropdownOpen,
])

  return (
    <Paper>
      <div
        style={{
          border: '1px solid rgba(234, 231, 242, 1)',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '15px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 35,
          }}
        >
          <Title>{surveyData?.data?.title}</Title>

          <BackButton
            color={userCompany?.button_color}
            onClick={() => {
              navigate(paths.surveys)
            }}
          >
            <LuArrowLeft />
            {formatMessage({
              id: 'surveys.backTo',
              defaultMessage: 'BACK TO SURVEYS',
            })}
          </BackButton>
        </div>

        <div style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)' }} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '30px 15px 0 25px',
          }}
        >
          <div />
          {canDo('survey: export survey result') && (
            <Button appearance="primary" onClick={() => setOpenModal(true)}>
              {formatMessage({
                id: 'surveys.exportSurvyeResults',
                defaultMessage: 'Export survey results',
              })}
            </Button>
          )}
        </div>
        <SurveyList searchTerm={''} currentPage={1} />
      </div>

      {openModal && (
        <ConfirmationModal
          header={
            isPowerPointLoading ? (
              <PowerPointSpinner />
            ) : (
             <ModalContent
              setSelectedType={setSelectedType}
              selectedType={selectedType}
              setSelectedDepartments={setSelectedDepartments}
              selectedDepartments={selectedDepartments}
              setSelectedLanguage={setSelectedLanguage}
              selectedLanguage={selectedLanguage}
              deparmentDropdownOpen={deparmentDropdownOpen}
              setDepartmentDropdownOpen={setDepartmentDropdownOpen}
              languageDropdownOpen={languageDropdownOpen}
              setLanguageDropdownOpen={setLanguageDropdownOpen}
              departments_tree={userInfo?.data?.departments_tree}
            /> 
            )
          }
          onConfirm={() => handleExportFile(surveyId)}
          onCancel={() => {
            setOpenModal(false)
            setSelectedType('')
            setSelectedDepartments(null)
            setSelectedLanguage('')
          }}
          isLoading={isPowerPointLoading || isExcelLoading}
          isLoadingPowerPoint={isPowerPointLoading}
        />
      )}

      {showToastMessage && (
        <Toast
          message={formatMessage({
            id: 'taskType.error',
            defaultMessage: 'Please fill all the required fields',
          })}
        />
      )}
    </Paper>
  )
}
export default SurveyView
